/**
 * Service responsável por gerenciar operações de CRUD de processos, tanto online quanto offline.
 * Este serviço suporta criação, atualização, exclusão e sincronização de processos,
 * além de gerenciar a persistência offline usando IndexedDB.
 * Sincroniza processos armazenados offline quando a conexão é restaurada e inclui
 * lógica de retentativa em caso de falha de sincronização.
 */
import { Injectable, OnDestroy } from '@angular/core';
import { Process, ProcessStatus } from 'src/app/core/models/process.model';
import { BaseService } from 'src/app/core/services/BaseService/base.service';
import {
  Observable,
  throwError,
  of,
  Subject,
  from,
  firstValueFrom,
} from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from 'src/app/core/services/AuthService/auth.service';
import {
  catchError,
  tap,
  debounceTime,
  finalize,
  mergeMap,
  map,
  switchMap,
} from 'rxjs/operators';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { IndexedDbService } from 'src/app/core/services/IndexedDbService/indexed-db.service';
import { CacheService } from 'src/app/core/services/CacheService/cache.service';
import { JSONSchema } from '@ngx-pwa/local-storage';
import { SyncService } from 'src/app/core/services/SyncService/syncService.service';
import { URLBuilder } from 'src/app/core/utils/url-builder';
import { ErrorHandlingService } from 'src/app/core/services/ErrorHandlingService/errorHandlingService.service';
import { OnlineOfflineService } from 'src/app/core/services/Online-Offline/online-offline.service';

@Injectable({
  providedIn: 'root',
})
export class ProcessService extends BaseService<Process> implements OnDestroy {
  protected localStorageKey = 'processes';
  private cache: Map<string, { data: any; expiration: number }> = new Map();
  private syncSubject = new Subject<void>();
  private onlineStatusSubscription: any;

  constructor(
    protected override http: HttpClient,
    protected override authService: AuthService,
    protected override indexedDbService: IndexedDbService,
    private syncService: SyncService,
    private errorHandlingService: ErrorHandlingService,
    private cacheService: CacheService,
    private onlineOfflineService: OnlineOfflineService,
    private snackBar: MatSnackBar,
    private urlBuilder: URLBuilder
  ) {
    super(http, authService, indexedDbService);
    this.initSyncProcesses();
    this.urlBuilder = new URLBuilder();
  }

  ngOnInit(): void {
    this.cacheService.startCacheCleanup();

    // Subscribe to online/offline status updates
    this.onlineStatusSubscription =
      this.onlineOfflineService.onlineStatus.subscribe((isOnline) => {
        this.handleOnlineStatus(isOnline); // Handle online/offline changes
      });
  }

  private initSyncProcesses(): void {
    this.syncSubject
      .pipe(debounceTime(300))
      .subscribe(() => this.syncProcesses());
    window.addEventListener('online', () => this.syncSubject.next());
  }

  ngOnDestroy(): void {
    this.onlineStatusSubscription?.unsubscribe(); // Unsubscribe from online/offline status updates
    window.removeEventListener('online', () => this.syncSubject.next());
  }

  // Define handleOnlineStatus to manage snackbar notifications and trigger sync
  private handleOnlineStatus(isOnline: boolean): void {
    if (isOnline) {
      this.showSnackbar('Você está online!');
      this.syncSubject.next(); // Start sync process when back online
    } else {
      this.showSnackbar(
        'Você está offline! Algumas funcionalidades podem não estar disponíveis.'
      );
    }
  }

  createOrUpdateProcess(process: Process): Observable<Process> {
    return this.isOnline()
      ? this.saveProcessOnline(process).pipe(map(() => process))
      : this.saveProcessOffline(process);
  }

  private isOnline(): boolean {
    return this.onlineOfflineService.isOnline();
  }

  private storeProcessLocally(savedProcess: Process): void {
    this.indexedDbService.saveProcess('processes', savedProcess).subscribe();
  }

  private saveProcessOnline(process: Process): Observable<boolean> {
    const url = this.urlBuilder.buildEndpoint(environment.processes.create);
    const token = this.authService.getToken();

    if (!token) {
      this.errorHandlingService.handleError(
        new Error('Token inválido ou expirado'),
        'Falha ao salvar processo online.'
      );
      return of(false);
    }

    const headers = this.getAuthHeaders(token);

    return this.http.post<Process>(url, process, { headers }).pipe(
      tap((savedProcess) => {
        this.storeProcessLocally(savedProcess); // Save to IndexedDB
        this.showSnackbar('Processo salvo online com sucesso!');
      }),
      map(() => true), // Map to true upon successful save
      catchError((err) => {
        console.error('Erro ao salvar o processo online:', err.message);
        this.errorHandlingService.handleHttpError(err);
        return of(false);
      })
    );
  }

  private showSnackbar(message: string): void {
    this.snackBar.open(message, 'Fechar', { duration: 3000 });
  }

  private saveProcessOffline(processData: Process): Observable<Process> {
    return this.indexedDbService
      .saveProcess('offlineProcesses', processData)
      .pipe(
        tap(() => {
          this.cacheService.setCache(
            `offline_process_${processData.id}`,
            processData,
            86400
          ); // Cache com TTL de 24 horas
          this.snackBar.open('Processo salvo offline.', 'Fechar', {
            duration: 3000,
          });
        }),
        map(() => processData), // Retorna o próprio processData
        catchError((err) => {
          this.errorHandlingService.handleError(
            err,
            'Falha ao salvar processo offline.'
          );
          return throwError(
            () => new Error('Falha ao salvar processo offline.')
          );
        })
      );
  }

  public syncProcesses(): Observable<void> {
    return this.syncService.syncOfflineProcesses();
  }

  listProcesses(
    idTipoProcesso: number,
    token: string,
    offset: number = 0,
    limit: number = 10,
    meusprocessos: boolean = true,
    exibiretapas: boolean = true,
    filter: string = ''
  ): Observable<Process[]> {
    const url = `https://quasar.des.arbor.quasar.tec.br/arbor/service/processo/${idTipoProcesso}/listar`;
  
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      Accept: 'application/json'
    });
  
    let params = new HttpParams()
      .set('offset', offset.toString())
      .set('limit', limit.toString())
      .set('meusprocessos', meusprocessos.toString())
      .set('exibiretapas', exibiretapas.toString());
  
    if (filter) {
      params = params.set('filter', filter);
    }
  
    return this.http.get<Process[]>(url, { headers, params });
  }


  private mapProcessResponse(process: any): Process {
    return {
      id: process.id,
      idIntegracao: process.idIntegracao || 0,
      codigo: process.codigo || '',
      fase: process.fase || '',
      descricao: process.descricao || '',
      numeroProcesso: process.numeroProcesso,
      idTipoProcesso: process.idTipoProcesso || 0,
      nomeTipoProcesso: process.nomeTipoProcesso || '',
      idModalidade: process.idModalidade || 0,
      idModalidadeIntegracao: process.idModalidadeIntegracao || 0,
      idModalidadeGrupo: process.idModalidadeGrupo || 0,
      idModalidadeGrupoIntegracao: process.idModalidadeGrupoIntegracao || 0,
      empresa: {
        id: process.empresa?.id,
        idIntegracao: process.empresa?.idIntegracao || 0,
        codigo: process.empresa?.codigo || '',
        cpfCnpj: process.empresa?.cpfCnpj || '',
        nome: process.empresa?.nome || '',
        email: process.empresa?.email,
        inscricaoMunicipal: process.empresa?.inscricaoMunicipal || '',
        endereco: {
          id: process.empresa?.endereco?.id || 0,
          cep: process.empresa?.endereco?.cep || '',
          logradouro: process.empresa?.endereco?.logradouro || '',
          numero: process.empresa?.endereco?.numero || '',
          complemento: process.empresa?.endereco?.complemento,
          bairro: process.empresa?.endereco?.bairro || '',
          cidade: process.empresa?.endereco?.cidade || '',
          referencia: process.empresa?.endereco?.referencia,
          uf: process.empresa?.endereco?.uf || '',
          principal: process.empresa?.endereco?.principal || true,
          tipoEndereco: process.empresa?.endereco?.tipoEndereco || 'COMERCIAL'
        },
        enderecos: process.empresa?.enderecos || [],
        telefone: process.empresa?.telefone,
        situacao: process.empresa?.situacao
      },
      imovel: {
        id: process.imovel?.id || 0,
        idIntegracao: process.imovel?.idIntegracao || 0,
        codigo: process.imovel?.codigo,
        inscricaoImobiliaria: process.imovel?.inscricaoImobiliaria,
        unidade: process.imovel?.unidade,
        descricao: process.imovel?.descricao,
        situacao: process.imovel?.situacao,
        tipoImovel: process.imovel?.tipoImovel,
        apartamento: process.imovel?.apartamento,
        bloco: process.imovel?.bloco,
        garagem: process.imovel?.garagem,
        sala: process.imovel?.sala,
        loja: process.imovel?.loja,
        lote: process.imovel?.lote,
        matricula: process.imovel?.matricula,
        quadra: process.imovel?.quadra,
        setor: process.imovel?.setor,
        secao: process.imovel?.secao,
        endereco: {
          id: process.imovel?.endereco?.id || 0,
          cep: process.imovel?.endereco?.cep || '',
          logradouro: process.imovel?.endereco?.logradouro || '',
          numero: process.imovel?.endereco?.numero || '',
          complemento: process.imovel?.endereco?.complemento,
          bairro: process.imovel?.endereco?.bairro || '',
          cidade: process.imovel?.endereco?.cidade || '',
          referencia: process.imovel?.endereco?.referencia,
          uf: process.imovel?.endereco?.uf || '',
          principal: process.imovel?.endereco?.principal || true,
          tipoEndereco: process.imovel?.endereco?.tipoEndereco || 'COMERCIAL'
        },
        enderecos: process.imovel?.enderecos || [],
        proprietarios: process.imovel?.proprietarios || []
      },
      dataDeInicio: process.dataDeInicio || new Date().toISOString(),
      situacao: process.situacao || ProcessStatus.Aguardando,
      etapas: process.etapas || [],
      dueDays: process.dueDays,
      registerDate: process.registerDate
    };
  }

  private makeListProcessesRequest(
    idTipoProcesso: number,
    token: string
  ): Observable<Process[]> {
    const url = `https://quasar.des.arbor.quasar.tec.br/arbor/service/processo/${idTipoProcesso}/listar`;
    console.log('URL final para listProcesses:', url);

    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      accept: 'application/json',
    });

    return this.http.get<Process[]>(url, { headers });
  }

  private getSchema(): JSONSchema {
    return {
      type: 'object',
      properties: {
        id: { type: 'number' },
        name: { type: 'string' },
        // Adicione outras propriedades conforme necessário
      },
      required: ['id', 'name'], // Ajuste as propriedades obrigatórias conforme necessário
    };
  }

  public buildProcess(processTypeData: any, processData: any): Process {
    const process: Process = {
      idIntegracao: 1,
      idTipoProcesso: 1,
      descricao: 'Process description',
      idModalidade: 1,
      idModalidadeGrupo: 1,
      empresa: {
        id: 1,
        idIntegracao: 1,
        codigo: 'empresa-code',
        cpfCnpj: '12345678000100',
        inscricaoMunicipal: '123456',
        endereco: {
          id: 0,
          cep: '12345678',
          logradouro: 'Rua Teste',
          numero: '123',
          bairro: 'Centro',
          cidade: 'Cidade Teste',
          uf: 'SP',
          principal: true,
          tipoEndereco: 'COMERCIAL',
        },
        nome: '',
      },
      dataDeInicio: new Date().toISOString(), // Include if mandatory
      situacao: ProcessStatus.Ativo, // Include if mandatory
      id: 0,
      fase: '',
      numeroProcesso: 'PROC-123456',
      imovel: {
        id: 0,
        idIntegracao: 0,
        codigo: 'imovel-code',
        endereco: {
          id: 0,
          cep: '12345678',
          logradouro: 'Rua Imovel',
          numero: '456',
          bairro: 'Bairro Imovel',
          cidade: 'Cidade Imovel',
          uf: 'SP',
          principal: true,
          tipoEndereco: 'COMERCIAL',
        },
      },
    };

    console.log('Built Process:', process);
    return process;
  }

  saveOrCreateProcess(
    process: Process,
    schema: JSONSchema
  ): Observable<Process | null> {
    if (!navigator.onLine) {
      this.saveProcess(process, schema);
      return of(null); // Retorna nulo quando salvo offline
    } else {
      return this.createProcess(process); // Retorna o processo criado online
    }
  }

  private syncSingleProcess(process: Process): Observable<void> {
    const url = this.urlBuilder.buildEndpoint(environment.processes.create);

    return this.http.post<Process>(url, process).pipe(
      tap(() => {
        console.log(`Process ID: ${process.id} synced.`);
        this.indexedDbService
          .removeProcess('offlineProcesses', process.id)
          .subscribe();
        this.showSnackbar(`Processo ${process.id} sincronizado com sucesso!`);
      }),
      map(() => void 0),
      catchError((error) => {
        console.error(`Erro ao sincronizar o processo ${process.id}:`, error);
        return of(void 0);
      })
    );
  }

  private async retrySync(
    process: Process,
    maxAttempts = 5,
    maxDelay = 32000
  ): Promise<void> {
    let attempts = 0;
    let delay = 1000; // Tempo inicial de espera (1 segundo)

    while (attempts < maxAttempts && delay <= maxDelay) {
      try {
        await this.syncSingleProcess(process);
        console.log(`Process ID: ${process.id} synced successfully.`);
        return; // Saída após sincronização bem-sucedida
      } catch (error) {
        attempts++;
        console.warn(
          `Error syncing process ID: ${process.id}. Retrying (${attempts}/${maxAttempts})...`
        );
        await this.delay(delay);
        delay = Math.min(delay * 2, maxDelay); // Aumenta o delay exponencialmente, limitado ao máximo
      }
    }

    console.error(
      `Failed to sync process ID: ${process.id} after ${maxAttempts} attempts.`
    );
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return !payload.exp || payload.exp < Date.now() / 1000;
    } catch (e) {
      this.logError('Error verifying token expiration:', e);
      return true;
    }
  }

  createBatch(processes: Process[]): Observable<void> {
    const url = this.urlBuilder.buildEndpoint('/processes/batch');
    const token = this.authService.getToken();

    if (!token) {
      return this.handleNoToken();
    }

    const headers = this.getAuthHeaders(token);

    return this.http.post<void>(url, processes, { headers }).pipe(
      tap(() => {
        console.log('Lote de processos enviado com sucesso.');
      }),
      catchError((err) => {
        console.error('Erro ao enviar lote de processos:', err);
        return throwError(
          () => new Error('Falha ao enviar lote de processos.')
        );
      })
    );
  }

  /**
   * Creates a process, using offline storage if offline.
   */
  createProcess(processData: Process): Observable<Process> {
    return this.isOnline()
      ? this.createProcessOnline(processData)
      : this.saveProcessOffline(processData);
  }

  private createProcessOnline(processData: Process): Observable<Process> {
    const url = this.urlBuilder.buildEndpoint(environment.processes.create);
    const token = this.authService.getToken();

    console.log('Attempting to create process online');
    console.log('URL:', url);
    console.log('Token:', token);
    console.log('Process Data:', processData);

    if (!token || this.authService.isTokenExpired(token)) {
      const errorResponse = new HttpErrorResponse({
        error: 'Token inválido ou expirado',
        status: 401,
        statusText: 'Unauthorized',
        url,
      });

      console.error('Token inválido ou expirado');
      this.errorHandlingService.handleHttpError(errorResponse);
      return throwError(() => errorResponse);
    }

    const headers = this.getAuthHeaders(token);
    console.log('Headers:', headers);

    return this.http.post<Process>(url, processData, { headers }).pipe(
      tap((savedProcess) => {
        console.log('Processo salvo com sucesso no servidor:', savedProcess);
        this.indexedDbService
          .saveProcess('processes', savedProcess)
          .subscribe();
        this.cacheService.setCache(
          `process_${savedProcess.id}`,
          savedProcess,
          3600
        );
        this.snackBar.open('Processo criado com sucesso!', 'Fechar', {
          duration: 3000,
        });
      }),
      catchError((err) => {
        console.error('Erro ao criar o processo online:', err);
        this.errorHandlingService.handleHttpError(err);
        return throwError(() => new Error('Falha ao criar processo online.'));
      }),
      finalize(() => console.log('Process creation finalized'))
    );
  }

  public getProcessesOffline(schema: JSONSchema): Observable<Process[]> {
    return this.getItemsOffline(schema);
  }

  public saveProcess(process: Process, schema: JSONSchema): void {
    this.saveItem(process, schema); // Método herdado de BaseService
  }

  public deleteProcess(id: number, schema: JSONSchema): Observable<void> {
    return this.deleteItem(id, 'id', schema); // Método herdado de BaseService
  }

  getProcessTypes(): Observable<{ id: number; name: string }[]> {
    const url = this.urlBuilder.buildEndpoint(
      environment.processTypes.openingTypes
    );
    const token = this.authService.getToken();

    if (!token) {
      return this.handleNoToken();
    }

    const headers = this.getAuthHeaders(token);

    return this.http.get<{ id: number; name: string }[]>(url, { headers }).pipe(
      tap((data) => {
        console.log('Process types fetched successfully.');
        this.cacheService.setCache('processTypes', data, 300); // Cache update with expiration
      }),
      catchError((err) => {
        this.showSnackbar('Failed to fetch process types.');
        return this.handleHttpError(err);
      })
    );
  }

  syncProcessesBatch(
    processes: Process[],
    batchSize: number = 10
  ): Observable<void> {
    const batches = this.chunkArray(processes, batchSize);
    let failedBatches: Process[][] = [];

    return from(batches).pipe(
      mergeMap((batch, index) => {
        const url = this.urlBuilder.buildEndpoint('/processes/sync');
        return this.http.post(url, batch).pipe(
          tap(() =>
            console.log(
              `Batch ${index + 1}/${batches.length} synced successfully.`
            )
          ),
          map(() => void 0),
          catchError((err) => {
            console.error(
              `Error syncing batch ${index + 1}/${batches.length}:`,
              err
            );
            failedBatches.push(batch);
            return of(void 0);
          })
        );
      }, 3),
      finalize(() => {
        if (failedBatches.length > 0) {
          console.warn(
            `${failedBatches.length} batches failed to sync. Retrying...`
          );
          this.retryFailedBatches(failedBatches);
        } else {
          console.log('All batches synced successfully.');
        }
      })
    );
  }

  protected override getAuthHeaders(token: string): HttpHeaders {
    return new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    });
  }

  private retryFailedBatches(failedBatches: Process[][]): void {
    from(failedBatches)
      .pipe(
        mergeMap((batch, index) => {
          const url = this.urlBuilder.buildEndpoint('/processes/sync');
          return this.http.post(url, batch).pipe(
            tap(() =>
              console.log(
                `Retry batch ${index + 1}/${
                  failedBatches.length
                } synced successfully.`
              )
            ),
            catchError((err) => {
              console.error(
                `Error retrying batch ${index + 1}/${failedBatches.length}:`,
                err
              );
              return of(void 0);
            })
          );
        }, 3),
        finalize(() => console.log('Retry of failed batches completed'))
      )
      .subscribe();
  }

  private chunkArray<T>(array: T[], size: number): T[][] {
    return Array.from({ length: Math.ceil(array.length / size) }, (_, i) =>
      array.slice(i * size, i * size + size)
    );
  }

  private logError(message: string, error?: any): void {
    console.error(message, error);
  }

  protected override handleNoToken(): Observable<never> {
    console.error('Token not found. Redirecting to login.');
    return throwError(() => new Error('Token not found'));
  }

  protected override handleHttpError(
    error: HttpErrorResponse
  ): Observable<never> {
    const errorMessage = this.getErrorMessage(error);
    this.errorHandlingService.handleError(error, errorMessage);
    return throwError(() => new Error(errorMessage));
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    console.error('HTTP Error:', error);
    const errorMessage = this.getErrorMessage(error);
    this.logError(`Request error [${error.status}]:`, error);
    this.showSnackbar(errorMessage);
    return throwError(() => new Error(errorMessage));
  }

  private getErrorMessage(error: HttpErrorResponse): string {
    switch (error.status) {
      case 400:
        return 'Requisição inválida. Verifique os dados enviados.';
      case 401:
        return 'Não autorizado. Faça login novamente.';
      case 403:
        return 'Acesso negado. Você não tem permissão.';
      case 404:
        return 'Recurso não encontrado.';
      case 500:
        return 'Erro no servidor. Tente novamente mais tarde.';
      default:
        return 'Falha ao processar a requisição. Tente novamente.';
    }
  }
}
